import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, Typography, Box, Chip } from '@material-ui/core';
import { CalendarToday } from '@material-ui/icons';

import Image from '../../common/Image';
import formatDate from '../../../utils/formatDate';
import TagCloud from '../../common/TagCloud';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: '5px',
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
        transition: 'all 0.4s ease',
        cursor: 'pointer',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '1px 1px 2px rgba(0,0,0,0.4)',
        },
    },
    content: {
        padding: 0,
    },
    mediaLeft: {
        marginRight: theme.spacing(1),
        flexShrink: 0,
    },
    image: {
        width: '96px',
        height: '96px',
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        }
    },
    userInfo: {
        flex: 1,
    },
    tagCloud: {
        marginTop: theme.spacing(2),
    },
    title: {
        color: theme.palette.primary.dark,
        marginBottom: theme.spacing(1),
        fontWeight: 600,
        fontSize: '1.8rem',
        lineHeight: 1.3,
        textDecoration: 'none !important',
    },
    area: {
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1),
        fontWeight: 500,
        fontSize: '1.6rem',
        fontFamily: '"Kosugi Maru", sans-serif',
        textDecoration: 'none !important',
    },
    dateContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1)
    },
    tagContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    categoryTag: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        fontWeight: 500
    },
    englishTag: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        fontWeight: 500
    },
    workTypeTag: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
        fontWeight: 500
    },
    tagCloud: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        '& .tag': {
            display: 'inline-block',
            margin: theme.spacing(0.5),
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.dark,
            padding: theme.spacing(0.5, 1),
            borderRadius: theme.shape.borderRadius,
            fontSize: '0.875rem',
            fontWeight: 500,
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            color: '#4a4a4a',
            display: 'inline-flex',
            fontSize: '0.75rem',
            height: '2em',
            justifyContent: 'center',
            lineHeight: '1.5',
            paddingLeft: '0.75em',
            paddingRight: '0.75em',
            whiteSpace: 'nowrap',
            textDecoration: 'none !important',
            '&.is-primary': {
                backgroundColor: '#00d1b2',
                color: '#fff',
            },
            '&.is-warning': {
                backgroundColor: '#ffdd57',
                color: 'rgba(0, 0, 0, 0.7)',
            },
            '&.is-success': {
                backgroundColor: '#23d160',
                color: '#fff',
            },
            '&.is-light': {
                backgroundColor: '#f5f5f5',
                color: '#7a7a7a',
            }
        }
    },
    marginBottomSmall: {
        marginBottom: '1.5rem !important',
    },
    marginTopSmall: {
        marginTop: '1.5rem !important',
    },
    flex: {
        display: 'flex',
    },
    flexWrap: {
        flexWrap: 'wrap',
    },
    flexAlign: {
        alignItems: 'center',
    },
    textWrap: {
        whiteSpace: 'pre-wrap',
    }
}));

const CurrentJobCard = ({ job }) => {
    const classes = useStyles();
    let imageUrl = job.mainImage
        ? job.mainImage.image_url
        : null;

    const renderJobImage = () => {
        if (!imageUrl) return null;
        return (
            <Box className={classes.mediaLeft}>
                <Box className={classes.image}>
                    <Image src={imageUrl} size={'200'} alt='Job image' />
                </Box>
            </Box>
        )
    }

    const renderEnglishLevel = englishLevel => {
        let content = englishLevel ? englishLevel : '不問';
        return (
            <Chip
                label={`英語力：${content}`}
                className={classes.englishTag}
                size="small"
            />
        )
    }

    return (
        <Card className={classes.root}>
            <CardContent className={classes.content}>
                <Box className={`${classes.flex} ${classes.marginBottomSmall}`}>
                    {renderJobImage()}
                    <Box className={classes.userInfo}>
                        <Typography variant="h6" component="h3" className={classes.title}>
                            {job.title}
                        </Typography>
                        <Typography variant="h5" className={classes.area}>
                            {job.area ? job.area.name : 'エリア指定無し'}
                        </Typography>
                        <Box className={classes.dateContainer}>
                            <CalendarToday fontSize="small" />
                            <Typography variant="body2">
                                {formatDate(job.updated_at)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box className={`${classes.tagContainer} ${classes.flex} ${classes.flexWrap}`}>
                    <Chip
                        label={job.jobCategory}
                        className={classes.categoryTag}
                        size="small"
                    />
                    {renderEnglishLevel(job.englishLevel)}
                    {job.workType && (
                        <Chip
                            label={job.workType}
                            className={classes.workTypeTag}
                            size="small"
                        />
                    )}
                </Box>
                <Box className={classes.tagCloud}>
                    <TagCloud tags={job.tags} />
                </Box>
            </CardContent>
        </Card>
    )
}

CurrentJobCard.propTypes = {
    job: PropTypes.object.isRequired
}

export default CurrentJobCard
